// Location: src/components/admin/schedule/ScheduleFrequencyInfo.tsx
// Purpose: Display schedule frequency, times, and action information
// Scope: Shows frequency details, scheduled times with timezone, and action counts

import React from 'react';
import { Schedule } from '@/types/schedules';

interface ScheduleFrequencyInfoProps {
	schedule: Schedule;
	getActionDisplay: () => string | null;
	pendingPostsCount?: number;
	displayReadyCount?: number;
}

// 🔄 Timezone Helper Functions
function getTimezoneAbbr(timezone: string): string {
	// Use the native Intl API to get the correct timezone abbreviation
	// This automatically handles DST for all timezones including Arizona
	const formatter = new Intl.DateTimeFormat('en-US', {
		timeZone: timezone,
		timeZoneName: 'short',
	});

	const parts = formatter.formatToParts(new Date());
	const tzPart = parts.find((part) => part.type === 'timeZoneName');

	if (tzPart) {
		return tzPart.value;
	}

	// Fallback for unknown timezones
	const fallbackMap: Record<string, string> = {
		'America/Los_Angeles': 'PST',
		'America/Denver': 'MST',
		'America/Phoenix': 'MST',
		'America/Chicago': 'CST',
		'America/New_York': 'EST',
		UTC: 'UTC',
	};

	return (
		fallbackMap[timezone] ||
		timezone.split('/').pop()?.substring(0, 3).toUpperCase() ||
		'TZ'
	);
}

export const ScheduleFrequencyInfo = React.memo(function ScheduleFrequencyInfo({
	schedule,
	getActionDisplay,
	pendingPostsCount = 0,
	displayReadyCount = 0,
}: ScheduleFrequencyInfoProps) {
	return (
		<div className='flex flex-col gap-2'>
			<div className='flex items-center gap-2'>
				<span className='text-lg font-semibold text-gray-900 dark:text-white'>
					Frequency:
				</span>
				<span className='text-pink-500 dark:text-pink-400 text-lg'>
					{schedule.schedule.frequency.toUpperCase()}
					{schedule.schedule.randomRange && (
						<span className='ml-2 text-sm text-orange-500 dark:text-orange-400'>
							(approximate)
						</span>
					)}
					{schedule.schedule.days &&
						schedule.schedule.days.length > 0 && (
							<span className='ml-2 text-muted-foreground/70 dark:text-muted-foreground/50 text-lg'>
								(
								{schedule.schedule.days
									.map((d: string) =>
										d.slice(0, 3).toUpperCase()
									)
									.join(', ')}
								)
							</span>
						)}
					<span className='ml-2 text-black/80 dark:text-white/80 text-lg'>
						(
						{schedule.schedule.times
							.map((time: string) => {
								const [hours, minutes] = time
									.split(':')
									.map(Number);

								// Create a simple time display without complex timezone conversion
								// The times in the database are already in the target timezone
								const hour12 =
									hours === 0
										? 12
										: hours > 12
											? hours - 12
											: hours;
								const ampm = hours >= 12 ? 'PM' : 'AM';
								const displayHour = hour12 === 0 ? 12 : hour12;

								// Get timezone abbreviation using native API
								const timezoneDisplay = getTimezoneAbbr(
									schedule.schedule.timezone
								);

								return `${displayHour}:${String(minutes).padStart(2, '0')} ${ampm} ${timezoneDisplay}`;
							})
							.join(', ')}
						)
					</span>
				</span>
			</div>
			<div className='flex items-center gap-2'>
				<span className='font-bold text-gray-900 dark:text-white'>
					Actions:
				</span>
				<span className='text-pink-500 dark:text-pink-400 text-lg'>
					{getActionDisplay() || 'N/A'}
				</span>
			</div>

			{/* Show ready comments count for scraper schedules */}
			{schedule.type === 'post_scraper' && (
				<div className='flex flex-col gap-1'>
					<div className='flex items-center gap-4'>
						<div className='flex items-center gap-2'>
							<span className='font-bold text-gray-900 dark:text-white'>
								Ready Comments:
							</span>
							<span className='text-green-500 dark:text-green-400 text-lg font-bold'>
								{displayReadyCount} comments
							</span>
						</div>
						{pendingPostsCount > 0 && (
							<div className='flex items-center gap-2'>
								<span className='font-bold text-gray-900 dark:text-white'>
									Pending Posts:
								</span>
								<span className='text-yellow-500 dark:text-yellow-400 text-lg font-bold'>
									{pendingPostsCount} posts
								</span>
							</div>
						)}
					</div>
					{displayReadyCount === 0 && pendingPostsCount === 0 && (
						<div className='text-sm text-gray-500 dark:text-gray-400 ml-2'>
							No comments ready to publish and no pending posts
						</div>
					)}
					{displayReadyCount === 0 && pendingPostsCount > 0 && (
						<div className='text-sm text-yellow-600 dark:text-yellow-400 ml-2'>
							No comments ready to publish. {pendingPostsCount}{' '}
							posts are imported but not converted to comments yet.
						</div>
					)}
					{/* Show last run status if available */}
					{schedule.lastRunStatus?.message && (
						<div className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
							<span className='font-medium'>
								Last source import run:
							</span>{' '}
							{schedule.lastRunStatus.message}
						</div>
					)}
				</div>
			)}

			{/* Show ready comments count for comment publishing schedules */}
			{(schedule.type === 'comments' ||
				schedule.type === 'profile_comment') && (
				<div className='flex flex-col gap-1'>
					<div className='flex items-center gap-4'>
						<div className='flex items-center gap-2'>
							<span className='font-bold text-gray-900 dark:text-white'>
								Ready Comments:
							</span>
							<span className='text-green-500 dark:text-green-400 text-lg font-bold'>
								{displayReadyCount} comments
							</span>
						</div>
						{pendingPostsCount > 0 && (
							<div className='flex items-center gap-2'>
								<span className='font-bold text-gray-900 dark:text-white'>
									Pending Posts:
								</span>
								<span className='text-yellow-500 dark:text-yellow-400 text-lg font-bold'>
									{pendingPostsCount} posts
								</span>
							</div>
						)}
					</div>
					{displayReadyCount === 0 && pendingPostsCount === 0 && (
						<div className='text-sm text-gray-500 dark:text-gray-400 ml-2'>
							No comments ready to publish and no pending posts
						</div>
					)}
					{displayReadyCount === 0 && pendingPostsCount > 0 && (
						<div className='text-sm text-yellow-600 dark:text-yellow-400 ml-2'>
							No comments ready to publish. {pendingPostsCount}{' '}
							posts need to be converted to comments first.
						</div>
					)}
				</div>
			)}
		</div>
	);
});
